import { trigger } from '@angular/animations';
import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { AssetDto, AssetVersionDto } from '@picflow/types';
import { fadeDownState } from '../../../animations/fade-down-state.animation';
import { Rect } from '../../../classes/rect.class';
import { AssetDisplayService } from '../../../services/asset-display.service';
import { AssetVersionUrlPipe } from '../../../shared/pipes/asset-version-url.pipe';

@Component({
  selector: 'app-image-viewer-preview',
  templateUrl: './image-viewer-preview.component.html',
  styleUrls: ['./image-viewer-preview.component.scss'],
  animations: [trigger('preview', fadeDownState)],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AssetVersionUrlPipe, AsyncPipe],
  providers: [AssetVersionUrlPipe],
})
export class ImageViewerPreviewComponent implements OnChanges {
  private assetDisplayService = inject(AssetDisplayService);
  private check = inject(ChangeDetectorRef);
  @ViewChild('previewContainer', { static: false }) previewContainer: ElementRef;
  @Input() asset: AssetDto;
  @Input() versionData: AssetVersionDto;
  @Input() showPreview: boolean;
  @Input() noAnimation: boolean;
  @Input() offsetX: number;
  @Input() offsetY: number;
  @Input() container: Rect;
  @Input() scale: number;
  @Output() onPreviewMove = new EventEmitter();
  crop: Rect;
  assetAspect: number;
  previewAspect: number;

  ngOnChanges(): void {
    const version = this.version;
    if (version.data) {
      this.assetAspect = version.data.width / version.data.height;
      this.previewAspect = (100 / version.data.width) * version.data.height;
    } else {
      this.assetAspect = 1;
      this.previewAspect = 100;
    }
    this.process();
  }

  public get version() {
    return this.versionData || this.asset?.version_data;
  }

  minimapDown(event: MouseEvent) {
    if (event.buttons !== 1) return;
    event.preventDefault();

    const el: HTMLElement = this.previewContainer.nativeElement;
    const bounds = el.getBoundingClientRect();
    const offsetX = event.clientX - bounds.left;
    const offsetY = event.clientY - bounds.top;

    const x = 0.5 - (1 / el.clientWidth) * offsetX;
    const y = 0.5 - (1 / el.scrollHeight) * offsetY;

    const containerAspect = this.container.width / this.container.height;
    const vertical = this.assetAspect < containerAspect;
    const boundedContainer = {
      width: vertical ? this.container.height * this.assetAspect : this.container.width,
      height: vertical ? this.container.height : this.container.width / this.assetAspect,
    };

    this.onPreviewMove.emit(new Rect(boundedContainer.width * x, boundedContainer.height * y));
  }

  process() {
    // eslint-disable-next-line prefer-const
    let { left, right, top, bottom, extraOffset, scaledContainer } = this.assetDisplayService.getScaledBounds(
      this.version,
      this.container,
      new Rect(this.offsetX, this.offsetY),
      this.scale
    );

    let w = scaledContainer.width;
    if (w > this.container.width) w = this.container.width;
    let h = scaledContainer.height;
    if (h > this.container.height) h = this.container.height;

    if (left < 0) {
      left = 0;
    } else if (right < 0) {
      left += right;
      right = 0;
    }
    if (top < 0) {
      top = 0;
    } else if (bottom < 0) {
      top += bottom;
      bottom = 0;
    }

    this.crop = new Rect(
      (100 / scaledContainer.width) * (left - extraOffset),
      (100 / scaledContainer.height) * (top - extraOffset),
      (100 / scaledContainer.width) * w,
      (100 / scaledContainer.height) * h
    );
    this.check.detectChanges();
  }
}
